import React, { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Check, Clock, Flame, Truck, PackageCheck } from "lucide-react";

const STAGES = [
    { id: "pending", label: "Ελήφθη", icon: Clock },
    { id: "preparing", label: "Ετοιμάζεται", icon: Flame },
    { id: "on_the_way", label: "Στον δρόμο", icon: Truck },
    { id: "delivered", label: "Παραδόθηκε", icon: PackageCheck },
];

export const OrderTracking = () => {
    const { id } = useParams();
    const [search] = useSearchParams();
    const sessionId = search.get("session_id");
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [paymentMsg, setPaymentMsg] = useState("");

    const fetchOrder = useCallback(async () => {
        try {
            const { data } = await api.get(`/orders/${id}`);
            setOrder(data);
        } catch {
            navigate("/");
        }
    }, [id, navigate]);

    // Polling for payment status if session_id present
    useEffect(() => {
        let attempts = 0;
        const max = 8;
        let timer;
        const poll = async () => {
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setPaymentMsg("Η πληρωμή ολοκληρώθηκε επιτυχώς!");
                    fetchOrder();
                    return;
                }
                if (data.status === "expired") {
                    setPaymentMsg("Η πληρωμή έληξε. Δοκίμασε ξανά.");
                    return;
                }
                attempts += 1;
                if (attempts < max) {
                    setPaymentMsg("Η πληρωμή σου ελέγχεται...");
                    timer = setTimeout(poll, 2000);
                } else {
                    setPaymentMsg("Δεν ήταν δυνατός ο έλεγχος της πληρωμής. Επικοινώνησε μαζί μας.");
                }
            } catch {
                setPaymentMsg("Σφάλμα ελέγχου πληρωμής.");
            }
        };
        if (sessionId) poll();
        return () => timer && clearTimeout(timer);
    }, [sessionId, fetchOrder]);

    useEffect(() => {
        fetchOrder();
        const interval = setInterval(fetchOrder, 8000);
        return () => clearInterval(interval);
    }, [fetchOrder]);

    if (!order) return <p className="text-center text-white/60 py-20">Φόρτωση...</p>;

    const activeIdx = STAGES.findIndex((s) => s.id === order.status);

    return (
        <div className="mx-auto max-w-4xl px-4 sm:px-8 py-12" data-testid="order-tracking-page">
            <p className="text-xs uppercase tracking-[0.3em] text-brand-yellow">Παραγγελία</p>
            <h1 className="headline text-4xl sm:text-5xl mt-2" data-testid="order-id">#{order.id.slice(0, 8).toUpperCase()}</h1>

            {paymentMsg && (
                <div className="mt-4 p-3 border border-brand-yellow/30 bg-brand-yellow/10 text-brand-yellow text-sm" data-testid="payment-msg">
                    {paymentMsg}
                </div>
            )}

            {/* Timeline */}
            <div className="mt-10 grid grid-cols-4 gap-4">
                {STAGES.map((s, i) => {
                    const Icon = s.icon;
                    const done = i <= activeIdx && order.status !== "cancelled";
                    return (
                        <div key={s.id} className="flex flex-col items-center text-center" data-testid={`stage-${s.id}`}>
                            <div className={`h-14 w-14 grid place-items-center rounded-full border-2 transition ${done ? "border-brand-red bg-brand-red text-white" : "border-white/15 text-white/40"}`}>
                                {done ? <Check size={20} /> : <Icon size={20} />}
                            </div>
                            <p className={`font-heading uppercase text-xs mt-2 tracking-wider ${done ? "text-white" : "text-white/40"}`}>{s.label}</p>
                        </div>
                    );
                })}
            </div>
            {order.status === "cancelled" && (
                <p className="text-center mt-6 text-brand-red font-heading uppercase">Η παραγγελία ακυρώθηκε</p>
            )}

            <div className="grid md:grid-cols-2 gap-6 mt-12">
                <section className="bg-[#16161a] border border-white/10 p-6">
                    <h2 className="font-heading uppercase tracking-wider mb-3 text-brand-red">Στοιχεία</h2>
                    <p className="text-sm"><span className="text-white/50">Όνομα:</span> {order.customer_name}</p>
                    <p className="text-sm"><span className="text-white/50">Τηλ:</span> {order.phone}</p>
                    <p className="text-sm"><span className="text-white/50">Διεύθυνση:</span> {order.address}</p>
                    {order.notes && <p className="text-sm mt-2"><span className="text-white/50">Σημειώσεις:</span> {order.notes}</p>}
                    <p className="text-sm mt-2">
                        <span className="text-white/50">Πληρωμή:</span> {order.payment_method === "card" ? "Online κάρτα" : "Στον διανομέα"} —{" "}
                        <span className={order.payment_status === "paid" ? "text-brand-yellow" : "text-white/70"}>
                            {order.payment_status === "paid" ? "Πληρωμένη" : "Εκκρεμής"}
                        </span>
                    </p>
                </section>
                <section className="bg-[#16161a] border border-white/10 p-6">
                    <h2 className="font-heading uppercase tracking-wider mb-3 text-brand-red">Σύνοψη</h2>
                    <ul className="space-y-1 text-sm">
                        {order.items.map((it) => (
                            <li key={it.item_id} className="flex justify-between">
                                <span>{it.quantity}× {it.name}</span>
                                <span>€{(it.price * it.quantity).toFixed(2)}</span>
                            </li>
                        ))}
                    </ul>
                    <div className="border-t border-white/10 my-3" />
                    <div className="text-sm space-y-1">
                        <div className="flex justify-between text-white/60"><span>Υποσύνολο</span><span>€{order.subtotal.toFixed(2)}</span></div>
                        {order.discount > 0 && <div className="flex justify-between text-brand-yellow"><span>Έκπτωση</span><span>-€{order.discount.toFixed(2)}</span></div>}
                        <div className="flex justify-between text-white/60"><span>Μεταφορικά</span><span>€{order.delivery_fee.toFixed(2)}</span></div>
                        <div className="flex justify-between text-lg font-heading mt-2"><span>Σύνολο</span><span className="text-brand-yellow">€{order.total.toFixed(2)}</span></div>
                    </div>
                </section>
            </div>
        </div>
    );
};
